import type { ReactNode } from 'react';

import SvgIcon from '@mui/material/SvgIcon';
import InfoIcon from '@mui/icons-material/Info';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import HelpIcon from '@mui/icons-material/Help';
import CheckIcon from '@mui/icons-material/CheckCircle';
import { styled, SxProps, Theme } from '../styles';

import Typography from '../Typography';
import { getTypeColors, getDynamicIconStyles } from './utils';

export type Type = 'info' | 'warning' | 'error' | 'question' | 'success';

export interface Props {
  className?: string;
  children: ReactNode;
  type?: Type;
  isSmall?: boolean;
  id?: string;
  useDangerousHtml?: boolean;
  isIllustrationHidden?: boolean;
  isIllustrationStickedOnTop?: boolean;
  illustrationElement?: ReactNode;
  sx?: SxProps<Theme>;
}

const Container = styled('div', {
  shouldForwardProp: (prop) => !['isSmall', 'isCentered'].includes(prop as string),
})<{ isSmall?: boolean; isCentered: boolean }>(({ theme: { spacing }, isSmall, isCentered }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: spacing(2),
  padding: isSmall ? spacing(1) : spacing(2),
  alignItems: isCentered ? 'center' : undefined,
}));

const ContentWrapper = styled('div')(({ theme: { palette } }) => ({
  minWidth: 0,
  color: palette.text.onMain,
  '& a': {
    color: 'inherit',
  },
}));

const Wrapper = styled('div')<{ type: Type }>(({ type }) => ({
  lineHeight: '1rem',
  ...getTypeColors(type),
}));

const iconByType = {
  info: InfoIcon,
  warning: WarningIcon,
  error: ErrorIcon,
  question: HelpIcon,
  success: CheckIcon,
};

const Callout = ({
  children,
  type = 'info',
  className = '',
  isSmall,
  id = '',
  illustrationElement = undefined,
  isIllustrationHidden,
  isIllustrationStickedOnTop,
  useDangerousHtml,
  sx = {},
}: Props) => {
  const Icon = iconByType[type];
  const variantTypography = isSmall ? 'bodySmall' : 'body';
  const isCentered = !isIllustrationStickedOnTop && Boolean(illustrationElement);

  return (
    <Wrapper sx={sx} className={className} id={id} type={type} data-testid="callout">
      <Container isSmall={isSmall} isCentered={isCentered} data-testid="callout-container">
        {!isIllustrationHidden && (
          <div data-testid="callout-illustration">
            {!illustrationElement ? (
              <SvgIcon component={Icon} sx={{ '&&': getDynamicIconStyles(isSmall, type) }} />
            ) : (
              illustrationElement
            )}
          </div>
        )}
        <ContentWrapper>
          {useDangerousHtml ? (
            <Typography
              variant={variantTypography}
              component="div"
              dangerouslySetInnerHTML={{
                __html: children as string,
              }}
            />
          ) : (
            <Typography variant={variantTypography} component="div">
              {children}
            </Typography>
          )}
        </ContentWrapper>
      </Container>
    </Wrapper>
  );
};

export default Callout;
